import { createRoot, Root } from 'react-dom/client';

import { JSX, useLayoutEffect, useRef } from 'react';

import { OrgChart } from 'd3-org-chart';

type Node<K extends string> = {
  id: string;
  parentId: string;
  nodeType: K;
  linked: boolean;
};

type OrgChartComponentProps<T extends Node<K>, K extends string> = {
  renderComponent: (node: T) => JSX.Element;
  data: T[];
  onNodeClick: (node: T) => void;
  nodeWidth?: (node: T) => number;
  nodeHeight?: (node: T) => number;
  childrenMargin?: number;
  siblingsMargin?: number;
  neighborMargin?: number;
  chartRef: React.RefObject<OrgChart<T> | null>; // Make chartRef a prop
};

const nodeRoots = new Map<HTMLElement, Root>();

export const OrgChartComponent = <T extends Node<K>, K extends string>({
  nodeWidth,
  nodeHeight,
  childrenMargin = 40,
  siblingsMargin = 30,
  neighborMargin = 20,
  renderComponent,
  onNodeClick,
  chartRef,
  data,
}: OrgChartComponentProps<T, K>): JSX.Element => {
  const d3Container = useRef<HTMLDivElement>(null);
  const lastSizeRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!data.length || !d3Container.current) return;
    if (!chartRef.current) {
      chartRef.current = new OrgChart();
      chartRef.current.container(d3Container.current as unknown as string);
    }
    const chart = chartRef.current;
    chart
      .container(d3Container.current as unknown as string) // d3-org-chart types are wrong here does accept element ref and string
      .layout('top')
      .compact(false)
      .nodeWidth((d) => (nodeWidth ? nodeWidth(d.data) : 90)) // Use the custom nodeWidth
      .nodeHeight((d) => (nodeHeight ? nodeHeight(d.data) : 120)) // Use the custom nodeHeight
      .childrenMargin(() => childrenMargin)
      .siblingsMargin(() => siblingsMargin)
      .neighbourMargin(() => neighborMargin)
      .onNodeClick((d) => {
        onNodeClick(d.data);
      })
      .nodeButtonHeight(() => 0)
      .nodeButtonWidth(() => 0)
      .buttonContent(() => {
        return ``;
      })
      .linkUpdate((node, index, nodes) => {
        const currentNode = nodes[index] as unknown as SVGPathElement; // don't know why types are wrong here
        if (node.data.linked) {
          currentNode.setAttribute('fill', 'none');
          currentNode.setAttribute('stroke', '#EFEFEF');
          currentNode.setAttribute('stroke-width', '2');
        }
      })
      .linkYOffset(0)
      .duration(400)
      .nodeContent((d) => {
        const nodeId = `orgNode-${d.data.id}`;

        requestAnimationFrame(() => {
          const nodeElement = document.getElementById(nodeId);

          if (!nodeElement) return;

          let root = nodeRoots.get(nodeElement);

          if (!root) {
            root = createRoot(nodeElement);
            nodeRoots.set(nodeElement, root);
          }

          const renderedComponent = renderComponent(d.data);
          root.render(renderedComponent);
        });

        return `<div id="${nodeId}" style="width:100%; height:100%;"></div>`;
      })
      .data(data)
      .render()
      .expandAll();
    // Resize observer to adjust chart dynamically
  }, [data, renderComponent]);

  useLayoutEffect(() => {
    if (!d3Container.current) return;

    // Threshold to ignore small changes (like scrollbar appearing/disappearing)
    const RESIZE_THRESHOLD = 20; // pixels

    const resizeObserver = new ResizeObserver((entries) => {
      const chart = chartRef.current;
      if (!chart) return;
      entries.forEach((entry) => {
        const { width, height } = entry.contentRect;
        const lastSize = lastSizeRef.current;

        // Calculate the difference from last known size
        const widthDiff = Math.abs(width - lastSize.width);
        const heightDiff = Math.abs(height - lastSize.height);

        // Only re-render if the change is significant (larger than scrollbar width)
        if (widthDiff > RESIZE_THRESHOLD || heightDiff > RESIZE_THRESHOLD) {
          lastSizeRef.current = { width, height };
          chart.container(d3Container.current as unknown as string).render();
        }
      });
    });

    resizeObserver.observe(d3Container.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const container = d3Container.current;

    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    }

    return () => {
      if (container) {
        container.removeEventListener('wheel', handleWheel, { capture: true });
      }
    };
  }, []);

  return <div ref={d3Container} />;
};

export default OrgChartComponent;
